// MicButton — the glowing "talk to Miko" control that floats on HQ.
//
// Visual states (per the brief):
//   idle      → solid neon blue, gentle breathing glow
//   listening → green, strong pulsing rings (it's hearing you)
//   thinking  → amber, spinning ring (Miko's brain is working)
//   speaking  → pink, solid + soft pulse (Miko is talking back)
//
// Tap when idle to talk. We capture the transcript, send it + live player stats
// to the AI brain, then show Miko's reply on screen AND speak it aloud. A small
// gear opens the voice on/off setting (default ON).

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameState, selectCash } from '@biz-kid/store';
import { useLifeComputed } from '../life/store.js';
import { useVoice } from './VoiceContext.js';
import { listen, speechRecognitionSupported } from './speech.js';
import { askMiko, type MikoStats } from './mikoBrain.js';

const COLORS: Record<string, string> = {
  idle: '#37b6ff',
  listening: '#2fe08a',
  thinking: '#ffba2b',
  speaking: '#ff5c8a',
};

export function MicButton() {
  const { micState, setMicState, speak, enabled, setEnabled } = useVoice();

  // ── live stats for the AI brain ──────────────────────────────────────────
  const cash = useGameState(selectCash);
  const computed = useLifeComputed();
  const servicesActive = useGameState((s) => s.services.active);
  const socialActive = useGameState((s) => s.social.active);
  const gamingActive = useGameState((s) => s.gaming.active);
  const tradeActive = useGameState((s) => s.trade.active);
  const hasStand = useGameState((s) => s.businesses.length > 0);
  const venture = tradeActive
    ? 'Trade & Resell'
    : gamingActive
      ? 'Gaming'
      : socialActive
        ? 'Social Media'
        : servicesActive
          ? 'Services'
          : hasStand
            ? 'Drop Store'
            : 'none yet';
  // Keep the newest stats in a ref so the async flow always sends current values.
  const statsRef = useRef<MikoStats>({ cash, empireValue: computed.totalWealth, freedomPct: computed.freedomPct, venture });
  statsRef.current = { cash, empireValue: computed.totalWealth, freedomPct: computed.freedomPct, venture };

  const [heard, setHeard] = useState<string | null>(null);
  const [reply, setReply] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const listenerRef = useRef<{ stop: () => void } | null>(null);
  const supported = speechRecognitionSupported();

  // Tidy any live recognition on unmount.
  useEffect(() => () => listenerRef.current?.stop(), []);

  const think = useCallback(
    async (transcript: string) => {
      setMicState('thinking');
      const { text } = await askMiko(transcript, statsRef.current);
      setReply(text);
      await speak(text); // sets micState speaking → idle
      setMicState('idle');
    },
    [setMicState, speak],
  );

  const onTap = useCallback(() => {
    // If Miko is mid-reply, a tap stops it. If already listening, stop early.
    if (micState === 'listening') {
      listenerRef.current?.stop();
      return;
    }
    if (micState === 'thinking' || micState === 'speaking') return;

    if (!supported) {
      // No STT on this browser — still be useful: speak a nudge.
      const s = statsRef.current;
      const line = `Voice input isn't on this browser, but you've got ${money(s.cash)}. Go run a week!`;
      setReply(line);
      void speak(line);
      return;
    }

    setHeard(null);
    setReply(null);
    setMicState('listening');
    listenerRef.current = listen({
      onResult: (t) => {
        setHeard(t);
        void think(t);
      },
      onError: () => {
        setMicState('idle');
        setReply("Didn't catch that — tap and try again. 🎤");
      },
    });
  }, [micState, supported, setMicState, speak, think]);

  const color = COLORS[micState] ?? COLORS.idle!;
  const label =
    micState === 'listening' ? 'Listening…' : micState === 'thinking' ? 'Thinking…' : micState === 'speaking' ? 'Miko…' : 'Ask Miko';

  return (
    <div style={{ position: 'fixed', right: 16, bottom: 18, zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
      {/* Reply / heard bubble */}
      <AnimatePresence>
        {(reply || heard) && (
          <motion.div
            key="bubble"
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            style={{
              maxWidth: 'min(78vw, 320px)', padding: '12px 14px', borderRadius: 16,
              background: '#241b42', border: `2.5px solid ${color}`,
              boxShadow: `0 0 0 1px ${color}55, 0 0 18px ${color}66, 0 8px 20px rgba(0,0,0,0.5)`,
              color: '#f4f1ff',
            }}
          >
            {heard && (
              <div style={{ fontSize: 11.5, fontWeight: 800, color: '#cbc2ec', marginBottom: reply ? 6 : 0, opacity: 0.9 }}>
                “{heard}”
              </div>
            )}
            {reply && <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>{reply}</div>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voice setting popover */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            style={{
              padding: '10px 12px', borderRadius: 14, background: '#241b42',
              border: '2px solid #37b6ff', boxShadow: '0 0 14px #37b6ff55', color: '#f4f1ff',
              display: 'flex', alignItems: 'center', gap: 10,
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 800 }}>🔊 Miko voice</span>
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              style={{
                border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 900, fontSize: 12,
                padding: '5px 12px', borderRadius: 999,
                background: enabled ? '#2fe08a' : '#3a3160', color: enabled ? '#0b0a16' : '#cbc2ec',
              }}
            >
              {enabled ? 'ON' : 'OFF'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Gear → voice setting */}
        <button
          type="button"
          aria-label="Voice settings"
          onClick={() => setShowSettings((v) => !v)}
          style={{
            width: 34, height: 34, borderRadius: 999, flexShrink: 0, cursor: 'pointer', fontFamily: 'inherit',
            background: '#241b42', border: '2px solid #4a3f74', color: '#cbc2ec', fontSize: 15,
          }}
        >
          ⚙︎
        </button>

        {/* The mic */}
        <motion.button
          type="button"
          aria-label={label}
          onClick={onTap}
          whileTap={{ scale: 0.92 }}
          animate={
            micState === 'listening'
              ? { scale: [1, 1.06, 1] }
              : micState === 'speaking'
                ? { scale: [1, 1.04, 1] }
                : { scale: 1 }
          }
          transition={
            micState === 'listening'
              ? { duration: 0.9, repeat: Infinity, ease: 'easeInOut' }
              : micState === 'speaking'
                ? { duration: 0.6, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.2 }
          }
          style={{
            position: 'relative', width: 62, height: 62, borderRadius: 999, flexShrink: 0,
            border: `3px solid ${color}`, background: '#241b42', color,
            boxShadow: `0 0 0 1px ${color}66, 0 0 22px ${color}aa, 0 8px 20px rgba(0,0,0,0.5)`,
            cursor: 'pointer', fontFamily: 'inherit', display: 'grid', placeItems: 'center', fontSize: 26,
          }}
        >
          {/* Pulsing rings while listening */}
          {micState === 'listening' && (
            <>
              <Ring color={color} delay={0} />
              <Ring color={color} delay={0.5} />
            </>
          )}
          {/* Spinner while thinking */}
          {micState === 'thinking' ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
              style={{ width: 26, height: 26, borderRadius: 999, border: `3px solid ${color}44`, borderTopColor: color }}
            />
          ) : (
            <span>{micState === 'speaking' ? '🔊' : '🎤'}</span>
          )}
        </motion.button>
      </div>
    </div>
  );
}

function Ring({ color, delay }: { color: string; delay: number }) {
  return (
    <motion.span
      initial={{ opacity: 0.5, scale: 1 }}
      animate={{ opacity: 0, scale: 2.1 }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut', delay }}
      style={{ position: 'absolute', inset: -3, borderRadius: 999, border: `2px solid ${color}`, pointerEvents: 'none' }}
    />
  );
}

function money(n: number): string {
  return `B$${Math.round(n).toLocaleString()}`;
}
